<script setup lang="ts">
// Panel 2: the season week by week, as the same five-outcome stack the
// headline split draws, against the visit counts the same MMWR week carried in
// the five seasons before it.
//
// The bars are translucent on purpose. The band is drawn first and reads
// through them, so a week's stack can be compared with its own history without
// flipping panels — the volume is the bar, the envelope is the past.
import { computed } from "vue";

import type { MapReviewWasteView } from "@/type/api";

import BarChart from "../charts/BarChart.vue";
import type { BarDataset, BarOverlayDataset } from "../charts/theme";
import { withAlpha } from "../charts/theme";
import ReportPanel from "../report/ReportPanel.vue";
import WasteOutcomeKey from "./WasteOutcomeKey.vue";
import { WASTE_OUTCOMES } from "./wasteTheme";

const props = defineProps<{
	view: MapReviewWasteView;
}>();

/** The band is deliberately the same grey the outcome split uses for the
 * checked-none reading: a prior-season envelope is context, not a verdict. */
const BAND_COLOR = "#8a9098";

/** The bars sit over the band, so they are mixed into the page rather than laid
 * on top of it: at full opacity the envelope would only be visible in the gaps
 * between weeks, which is where it carries the least information. */
const BAR_ALPHA = 0.72;

const hasWeeks = computed(() => props.view.weeks.length > 0);

const labels = computed(() => props.view.weeks.map((week) => `W${week.week}`));

const priorMin = computed(() => props.view.weeks.map((week) => week.priorMin));

const priorMax = computed(() => props.view.weeks.map((week) => week.priorMax));

const priorMean = computed(() =>
	props.view.weeks.map((week) => week.priorMean),
);

/** One bar series per outcome, in the report's own order, so the stack reads
 * bottom-up the way the split bar reads left-to-right.
 *
 * The segments are floating bars — each drawn from the running total to that
 * total plus its own count — rather than asking the axis to stack. Stacking at
 * the axis stacks every dataset on it, including the band and mean lines, which
 * would draw the history on top of the season instead of behind it. */
const outcomes = computed<BarDataset[]>(() => {
	const floor = props.view.weeks.map(() => 0);
	return WASTE_OUTCOMES.map((outcome) => {
		const data = props.view.weeks.map((week, index) => {
			const from = floor[index];
			const to = from + week[outcome.key];
			floor[index] = to;
			return [from, to];
		});
		return {
			type: "bar" as const,
			label: outcome.label,
			data: data as unknown as number[],
			backgroundColor: withAlpha(outcome.color, BAR_ALPHA),
			borderColor: withAlpha(outcome.color, 0.95),
			borderWidth: 0.5,
			barPercentage: 0.86,
			categoryPercentage: 0.94,
			// The five segments are one column, not five bars beside it: each is
			// already offset by the running total, so they share the category's
			// centre and stack by construction.
			grouped: false,
		};
	});
});

/** The overlay band first, then its mean, then the bars: the stack sits on top
 * of the band, exactly as the population trajectory stacks them. */
const datasets = computed<(BarDataset | BarOverlayDataset)[]>(() => [
	{
		type: "line" as const,
		label: "Five-season range",
		data: priorMax.value,
		borderColor: "transparent",
		backgroundColor: withAlpha(BAND_COLOR, 0.18),
		borderWidth: 0,
		pointRadius: 0,
		fill: "+1",
		band: true,
	},
	{
		type: "line" as const,
		label: "Five-season range floor",
		data: priorMin.value,
		borderColor: "transparent",
		backgroundColor: "transparent",
		borderWidth: 0,
		pointRadius: 0,
		fill: false,
		band: true,
	},
	{
		type: "line" as const,
		label: "Five-season average",
		data: priorMean.value,
		borderColor: BAND_COLOR,
		backgroundColor: "transparent",
		borderWidth: 1.2,
		borderDash: [4, 3],
		pointRadius: 0,
		pointHoverRadius: 3,
		fill: false,
	},
	...outcomes.value,
]);
</script>

<template>
	<section class="waste-weekly">
		<ReportPanel title="Through the season, week by week">
			<p class="panel-help">
				This season's visits by disease week, split the same way the headline
				split is. The shaded band behind the bars is what the same week carried
				in each of the five seasons before; the dashed line is their average.
			</p>

			<p v-if="!hasWeeks" class="panel-empty">
				No disease week in this season carries a visit.
			</p>
			<template v-else>
				<div class="panel-chart">
					<BarChart
						:labels="labels"
						:datasets="datasets"
						:show-legend="false"
					/>
				</div>
				<WasteOutcomeKey />
				<div class="legend">
					<span class="key">
						<span
							class="swatch band"
							:style="{ background: withAlpha(BAND_COLOR, 0.35) }"
						/>
						Five-season range
					</span>
					<span class="key">
						<span
							class="swatch dashed"
							:style="{ borderTopColor: BAND_COLOR }"
						/>
						Five-season average
					</span>
				</div>
			</template>

			<template #help>
				<p>
					Each segment is one outcome's visit count for that disease week; the
					segments sum to the week's visits. A treatment on any Feature of a
					Site marks the whole Site's visit treated, so the stack counts
					site-visits, not inspections.
				</p>
				<p>
					The band is the lowest and highest visit count any of the five prior
					seasons recorded in the same MMWR week; the dashed line is their mean.
					A week no prior season reached has no band, and the band breaks there
					rather than being filled with a guess.
				</p>
			</template>
		</ReportPanel>
	</section>
</template>

<style scoped>
.waste-weekly {
	grid-column: 1 / -1;
	min-width: 0;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.panel-chart {
	position: relative;
	height: 210px;
}

.legend {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 6px;
	font-size: 0.66rem;
	color: #6c757d;
}

.key {
	display: flex;
	align-items: center;
	gap: 5px;
}

.swatch {
	width: 14px;
	height: 3px;
	border-radius: 2px;
}

.swatch.band {
	height: 8px;
	border-radius: 1px;
}

.swatch.dashed {
	height: 0;
	border-top-width: 2px;
	border-top-style: dashed;
	background: transparent;
}
</style>
